/**
 * AnalysisTab — AI analysis results table.
 * - Breaks out of the 960px Processing page container so the full table is visible without scrolling.
 * - Custom Tooltip component replaces all native browser `title` popups.
 * - Gemini Notes column is compacted to 1 line / 180px max.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { verifyApi } from '../services/api';
import { AnalysisResponse, AnalysisResult } from '../types';

interface AnalysisTabProps {
  jobId: string;
  isActive: boolean;
}

interface RowState {
  audioUrl: string | null;
  audioLoading: boolean;
  audioError: string | null;
  transcript: string | null;
  transcriptLoading: boolean;
  transcriptError: string | null;
  transcriptUnavailable: boolean;
  showTranscript: boolean;
}

const defaultRowState = (): RowState => ({
  audioUrl: null, audioLoading: false, audioError: null,
  transcript: null, transcriptLoading: false, transcriptError: null,
  transcriptUnavailable: false, showTranscript: false,
});

// ── Custom Tooltip ────────────────────────────────────────────────────────────

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  maxWidth?: number;
}

const Tooltip: React.FC<TooltipProps> = ({ content, children, maxWidth = 280 }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const ref = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    setPos({ x: e.clientX, y: e.clientY });
  };

  const child = React.cloneElement(children, {
    onMouseEnter: (e: React.MouseEvent) => {
      setVisible(true);
      setPos({ x: e.clientX, y: e.clientY });
      children.props.onMouseEnter?.(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      setVisible(false);
      children.props.onMouseLeave?.(e);
    },
    onMouseMove: handleMouseMove,
  });

  return (
    <>
      {child}
      {visible && content && (
        <div
          ref={ref}
          style={{
            position: 'fixed',
            left: pos.x + 12,
            top: pos.y - 8,
            zIndex: 9999,
            pointerEvents: 'none',
            maxWidth,
          }}
        >
          <div style={{
            background: 'var(--navy-800)',
            color: 'var(--text)',
            fontSize: 12,
            lineHeight: 1.5,
            padding: '8px 12px',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>
            {content}
          </div>
          {/* Arrow */}
          <div style={{
            position: 'absolute',
            top: 10,
            left: -5,
            width: 0, height: 0,
            borderTop: '5px solid transparent',
            borderBottom: '5px solid transparent',
            borderRight: '5px solid var(--navy-800)',
          }} />
        </div>
      )}
    </>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [25, 50, 100];

export const AnalysisTab: React.FC<AnalysisTabProps> = ({ jobId, isActive }) => {
  const [results, setResults] = useState<AnalysisResult[]>([]);
  const [summary, setSummary] = useState<AnalysisResponse['summary'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const patchRow = useCallback((sid: string, patch: Partial<RowState>) => {
    setRowStates(prev => ({
      ...prev,
      [sid]: { ...(prev[sid] ?? defaultRowState()), ...patch },
    }));
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    const fetchAnalysis = async () => {
      try {
        const data = await verifyApi.getAnalysisStatus(jobId);
        setResults(data.results);
        setSummary(data.summary ?? null);
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch analysis', err);
        setLoading(false);
      }
    };
    if (isActive) {
      fetchAnalysis();
      interval = setInterval(fetchAnalysis, 3000);
    }
    return () => clearInterval(interval);
  }, [jobId, isActive]);

  const handleListenRecording = useCallback(async (row: AnalysisResult) => {
    const sid = row.call_sid;
    let shouldLoad = true;
    setRowStates(prev => {
      const current = prev[sid] ?? defaultRowState();
      if (current.audioUrl || current.audioLoading) {
        shouldLoad = false;
      }
      return prev;
    });
    if (!shouldLoad) return;
    patchRow(sid, { audioLoading: true, audioError: null });
    try {
      const url = row.recording_available
        ? await verifyApi.streamRecordingAsBlob(jobId, sid)
        : await verifyApi.fetchRecordingOnDemand(jobId, sid);
      patchRow(sid, { audioUrl: url, audioLoading: false });
    } catch (e: any) {
      patchRow(sid, {
        audioLoading: false,
        audioError: e?.response?.data?.detail || 'Recording not available',
      });
    }
  }, [jobId, patchRow]);

  const handleFetchTranscript = useCallback(async (sid: string) => {
    let alreadyLoading = false;
    setRowStates(prev => {
      const cur = prev[sid] ?? defaultRowState();
      if (cur.transcriptLoading || cur.transcript || cur.transcriptUnavailable) {
        alreadyLoading = true;
      }
      return prev;
    });
    if (alreadyLoading) return;
    patchRow(sid, { transcriptLoading: true, transcriptError: null, showTranscript: true });
    try {
      const res = await verifyApi.getTranscript(jobId, sid);
      if (res.available && res.text) {
        patchRow(sid, { transcript: res.text, transcriptLoading: false });
      } else {
        patchRow(sid, {
          transcriptLoading: false,
          transcriptUnavailable: true,
          transcriptError: res.message || 'No transcript available',
        });
      }
    } catch {
      patchRow(sid, { transcriptLoading: false, transcriptError: 'Failed to fetch transcript' });
    }
  }, [jobId, patchRow]);

  if (!isActive) return null;

  const total      = results.length;
  const completed  = results.filter(r => r.analysis_status === 'completed').length;
  const running    = results.filter(r => ['processing', 'queued'].includes(r.analysis_status)).length;
  const failed     = results.filter(r => ['failed', 'no_recording'].includes(r.analysis_status)).length;
  const summaryHealth = summary?.health;
  const summaryBlocked = summaryHealth === 'blocked';
  const summaryWorking = summaryHealth === 'working';
  const summaryComplete = summaryHealth === 'complete';

  // Pagination
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage   = Math.min(page, totalPages);
  const sliceStart = (safePage - 1) * pageSize;
  const sliceEnd   = sliceStart + pageSize;
  const pageItems  = results.slice(sliceStart, sliceEnd);
  const visibleEnd = Math.min(sliceEnd, total);

  const goTo = (p: number) => setPage(Math.max(1, Math.min(p, totalPages)));
  const handlePageSize = (n: number) => { setPageSize(n); setPage(1); };

  // Owner check only runs for VOICEMAIL and SWITCHBOARD
  const ownerCheckCount = results.filter(r =>
    ['VOICEMAIL', 'SWITCHBOARD'].includes((r.final_category || r.initial_category || '').toUpperCase())
  ).length;

  return (
    <div style={{
      background: 'var(--surface)',
      border: '2px solid var(--border)',
      borderRadius: 0,
      boxShadow: '5px 5px 0 rgba(0,0,0,0.55)',
    }}>

      {/* Header */}
      <div style={{ padding: '16px 24px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-strong)' }}>🤖 AI Analysis</h3>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--text-soft)' }}>
              Gemini reclassifies ambiguous recordings.
              {ownerCheckCount > 0 && (
                <span style={{ marginLeft: 8, color: 'var(--indigo)', fontWeight: 600 }}>
                  Owner check active on {ownerCheckCount} number{ownerCheckCount !== 1 ? 's' : ''} (Voicemail / Switchboard only)
                </span>
              )}
            </p>
          </div>
          {total > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <StatChip label="Total"   value={total}     color="var(--indigo)" bg="var(--status-analysis-processing-bg)" />
              <StatChip label="Done"    value={completed} color="var(--status-success-fg)" bg="var(--status-success-bg)" />
              {running > 0 && <StatChip label="Running" value={running} color="var(--status-analysis-processing-fg)" bg="var(--status-analysis-processing-bg)" pulse />}
              {failed  > 0 && <StatChip label="Failed"  value={failed}  color="var(--status-analysis-failed-fg)" bg="var(--status-analysis-failed-bg)" />}
            </div>
          )}
        </div>

        {total > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>Analysis progress</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-strong)' }}>{completed} / {total}</span>
            </div>
            <div style={{ background: 'rgba(200,120,32,0.15)', borderRadius: 6, height: 6, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${total > 0 ? (completed / total) * 100 : 0}%`,
                background: 'linear-gradient(90deg, var(--teal), var(--amber))',
                borderRadius: 6, transition: 'width 0.5s ease',
              }} />
            </div>
          </div>
        )}

        {summary && (
          <div style={{
            marginTop: 12,
            padding: '10px 12px',
            borderRadius: 6,
            border: `1px solid ${
              summaryBlocked ? 'var(--status-analysis-failed-fg)' :
              summaryWorking ? 'var(--status-analysis-processing-fg)' :
              'var(--status-success-fg)'
            }`,
            background: summaryBlocked ? 'var(--status-analysis-failed-bg)' :
              summaryWorking ? 'var(--status-analysis-processing-bg)' :
              'var(--status-success-bg)',
            color: summaryBlocked ? 'var(--status-analysis-failed-fg)' :
              summaryWorking ? 'var(--status-analysis-processing-fg)' :
              'var(--status-success-fg)',
            fontSize: 12,
            lineHeight: 1.45,
          }}>
            <strong>
              {summaryComplete ? 'Analysis complete' :
                summaryBlocked ? 'Analysis blocked' : 'Analysis running'}
            </strong>
            <span style={{ marginLeft: 8 }}>
              {summary.user_message}
            </span>
            {summary.writeback_status !== 'none' && (
              <span style={{ marginLeft: 8 }}>
                Writeback: <strong>{summary.writeback_status}</strong>
              </span>
            )}
            {summary.writeback_error && (
              <div style={{ marginTop: 4, color: 'var(--text-strong)' }}>
                {summary.writeback_error}
              </div>
            )}
          </div>
        )}

        {/* ── Pagination bar ── */}
        {total > 0 && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginTop: 12, flexWrap: 'wrap', gap: 8,
          }}>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
              Showing <strong>{sliceStart + 1}–{visibleEnd}</strong> of <strong>{total}</strong> rows
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>Rows per page:</span>
              {PAGE_SIZE_OPTIONS.map(n => (
                <button
                  key={n}
                  onClick={() => handlePageSize(n)}
                  style={{
                    padding: '2px 9px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                    border: `1.5px solid ${pageSize === n ? 'var(--teal)' : 'var(--border)'}`,
                    background: pageSize === n ? 'var(--teal)' : 'var(--surface-subtle)',
                    color: pageSize === n ? 'var(--navy)' : 'var(--muted)',
                    cursor: 'pointer',
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Table — no overflowX needed since we expanded the container */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: 'var(--surface-muted)' }}>
            <Th>Phone Number</Th>
            <Th>Initial</Th>
            <Th>Final AI</Th>
            <Th>Conf.</Th>
            <Th>Owner Match</Th>
            <Th>Gemini Notes</Th>
            <Th>Recording / Transcript</Th>
          </tr>
        </thead>
        <tbody>
          {loading && results.length === 0 ? (
            [1, 2, 3].map(i => (
              <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                {[1, 2, 3, 4, 5, 6, 7].map(j => (
                  <td key={j} style={{ padding: '10px 14px' }}>
                    <div style={{
                      height: 12, borderRadius: 4,
                      background: 'linear-gradient(90deg, rgba(200,120,32,0.08) 25%, rgba(200,120,32,0.15) 50%, rgba(200,120,32,0.08) 75%)',
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 1.5s infinite',
                      width: j === 6 ? '80%' : '60%',
                    }} />
                  </td>
                ))}
              </tr>
            ))
          ) : results.length === 0 ? (
            <tr>
              <td colSpan={7} style={{ padding: '40px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
                No calls queued for AI analysis yet.
              </td>
            </tr>
          ) : (
            pageItems.map(row => {
              const rs = rowStates[row.call_sid] ?? defaultRowState();
              const canReviewAudio = Boolean(row.call_sid) && row.analysis_status !== 'no_recording';
              const isFailed = ['failed', 'no_recording'].includes(row.analysis_status);

              return (
                <React.Fragment key={row.call_sid}>
                  <tr style={{
                    borderBottom: rs.showTranscript && rs.transcript ? 'none' : '1px solid var(--border-subtle)',
                    background: row.analysis_status === 'processing' ? 'var(--status-analysis-processing-bg)' : 'transparent',
                  }}>

                    {/* Phone Number */}
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      {isFailed && row.notes ? (
                        <Tooltip content={<><strong>Error</strong><br />{row.notes}</>} maxWidth={300}>
                          <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            fontWeight: 600, fontSize: 12, color: 'var(--status-danger-fg)', cursor: 'help',
                          }}>
                            {row.phone_number}
                            <span style={{ fontSize: 11 }}>ⓘ</span>
                          </div>
                        </Tooltip>
                      ) : (
                        <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text)' }}>
                          {row.phone_number}
                        </div>
                      )}
                      {row.call_sid && (
                        <div style={{ fontSize: 10, color: 'var(--text-faint)', fontFamily: 'monospace', marginTop: 1 }}>
                          {row.call_sid.slice(0, 18)}…
                        </div>
                      )}
                    </td>

                    {/* Initial Status */}
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <CategoryBadge category={row.initial_category} variant="subtle" />
                    </td>

                    {/* Final AI Status */}
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      {row.analysis_status === 'completed' && row.final_category
                        ? <CategoryBadge category={row.final_category} variant="bold" />
                        : <ProgressBadge status={row.analysis_status} errorNote={isFailed ? row.notes : null} />
                      }
                    </td>

                    {/* Confidence */}
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      {row.ai_confidence != null
                        ? <ConfidenceBadge value={row.ai_confidence} />
                        : <span style={{ color: 'var(--text-faint)' }}>—</span>
                      }
                    </td>

                    {/* Owner Match */}
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      {row.owner_verified === true ? (
                        <Tooltip content={
                          <>
                            <strong>Owner Confirmed</strong><br />
                            Name &quot;{row.owner_name}&quot; was detected in the greeting.
                          </>
                        }>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            padding: '2px 8px', borderRadius: 20,
                            fontSize: 11, fontWeight: 700,
                            background: 'var(--status-success-bg)', color: 'var(--status-success-fg)', cursor: 'help',
                          }}>
                            ✓ Owner
                          </span>
                        </Tooltip>
                      ) : row.owner_verified === false ? (
                        <Tooltip content={
                          <>
                            <strong>Not the Owner</strong><br />
                            {row.owner_name
                              ? `"${row.owner_name}" was not mentioned in the greeting.`
                              : 'Owner name was not found in the greeting.'}
                          </>
                        }>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            padding: '2px 8px', borderRadius: 20,
                            fontSize: 11, fontWeight: 600,
                            background: 'var(--status-neutral-bg)', color: 'var(--status-neutral-fg)', cursor: 'help',
                          }}>
                            ✗ Not Owner
                          </span>
                        </Tooltip>
                      ) : (
                        <span style={{ color: 'var(--text-faint)', fontSize: 12 }}>—</span>
                      )}
                    </td>

                    {/* Gemini Notes — compact: 1 line, 180px max */}
                    <td style={{ padding: '10px 14px', maxWidth: 180 }}>
                      {row.ai_notes ? (
                        <Tooltip content={row.ai_notes} maxWidth={320}>
                          <span style={{
                            display: 'block',
                            fontSize: 12,
                            color: 'var(--text-soft)',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            maxWidth: 180,
                            cursor: 'help',
                          }}>
                            {row.ai_notes}
                          </span>
                        </Tooltip>
                      ) : (
                        <span style={{ color: 'var(--text-faint)' }}>—</span>
                      )}
                    </td>

                    {/* Recording / Transcript */}
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        {canReviewAudio ? (
                          rs.audioUrl ? (
                            <audio controls src={rs.audioUrl} style={{ height: 28, width: 200 }} />
                          ) : rs.audioLoading ? (
                            <span style={{ fontSize: 11, color: 'var(--status-analysis-processing-fg)' }}>
                              <span style={{ animation: 'pulse-dot 1s infinite', display: 'inline-block' }}>●</span>
                              {' '}Loading…
                            </span>
                          ) : rs.audioError ? (
                            <Tooltip content={rs.audioError}>
                              <span style={{ fontSize: 11, color: 'var(--status-danger-fg)', cursor: 'help' }}>
                                ⚠ Error ⓘ
                              </span>
                            </Tooltip>
                          ) : (
                            <button
                              onClick={() => handleListenRecording(row)}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                background: 'var(--status-info-bg)', color: 'var(--status-info-fg)',
                                border: '1px solid var(--status-info-fg)', borderRadius: 4,
                                padding: '4px 10px', cursor: 'pointer',
                                fontSize: 11, fontWeight: 600,
                              }}
                            >
                              {row.recording_available ? '▶ Listen' : '⬇ Fetch'}
                            </button>
                          )
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--text-soft)', fontStyle: 'italic' }}>No recording</span>
                        )}

                        {row.call_sid && canReviewAudio && (
                          rs.transcriptUnavailable ? (
                            <Tooltip content={rs.transcriptError || 'No transcript available'}>
                              <span style={{ fontSize: 11, color: 'var(--muted)', cursor: 'help', textDecoration: 'underline dotted' }}>
                                No transcript ⓘ
                              </span>
                            </Tooltip>
                          ) : rs.transcriptLoading ? (
                            <span style={{ fontSize: 11, color: 'var(--muted)' }}>Fetching…</span>
                          ) : rs.transcript ? (
                            <button
                              onClick={() => patchRow(row.call_sid, { showTranscript: !rs.showTranscript })}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                fontSize: 11, color: 'var(--status-success-fg)', padding: 0,
                                textDecoration: 'underline', textAlign: 'left',
                              }}
                            >
                              {rs.showTranscript ? '▲ Hide' : '▼ Transcript'}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleFetchTranscript(row.call_sid)}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                fontSize: 11, color: 'var(--muted)', padding: 0,
                                textDecoration: 'underline', textAlign: 'left',
                              }}
                            >
                              Get Transcript
                            </button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Transcript expansion row */}
                  {rs.showTranscript && rs.transcript && (
                    <tr style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-muted)' }}>
                      <td colSpan={7} style={{ padding: '10px 16px 14px 32px' }}>
                        <p style={{ margin: '0 0 4px', fontSize: 10, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Transcript
                        </p>
                        <p style={{ margin: 0, fontSize: 12, color: 'var(--text)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {rs.transcript}
                        </p>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>

      <style>{`
        @keyframes shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position:  200% 0; }
        }
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }
      `}</style>

      {/* ── Bottom pagination nav ── */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          gap: 8, padding: '12px 24px', borderTop: '1px solid var(--border-subtle)',
          flexWrap: 'wrap',
        }}>
          <button
            onClick={() => goTo(safePage - 1)}
            disabled={safePage === 1}
            style={{
              padding: '5px 14px', borderRadius: 4, fontSize: 12, fontWeight: 600,
              border: '1.5px solid var(--border)',
              background: safePage === 1 ? 'var(--surface-muted)' : 'var(--surface-subtle)',
              color: safePage === 1 ? 'var(--text-faint)' : 'var(--muted)',
              cursor: safePage === 1 ? 'not-allowed' : 'pointer',
            }}
          >
            ← Previous
          </button>

          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', minWidth: 110, textAlign: 'center' }}>
            Page {safePage} of {totalPages}
          </span>

          <button
            onClick={() => goTo(safePage + 1)}
            disabled={safePage === totalPages}
            style={{
              padding: '5px 14px', borderRadius: 4, fontSize: 12, fontWeight: 600,
              border: '1.5px solid var(--border)',
              background: safePage === totalPages ? 'var(--surface-muted)' : 'var(--surface-subtle)',
              color: safePage === totalPages ? 'var(--text-faint)' : 'var(--muted)',
              cursor: safePage === totalPages ? 'not-allowed' : 'pointer',
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th style={{
    padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700,
    color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em',
    borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap',
  }}>
    {children}
  </th>
);

const CATEGORY_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  GOOD:        { bg: 'var(--category-good-bg)', color: 'var(--category-good-fg)', label: 'Human' },
  VOICEMAIL:   { bg: 'var(--category-voicemail-bg)', color: 'var(--category-voicemail-fg)', label: 'Voicemail' },
  SWITCHBOARD: { bg: 'var(--category-switchboard-bg)', color: 'var(--category-switchboard-fg)', label: 'Switchboard' },
  FAX:         { bg: 'var(--category-fax-bg)', color: 'var(--category-fax-fg)', label: 'Fax' },
  DEAD:        { bg: 'var(--category-dead-bg)', color: 'var(--category-dead-fg)', label: 'Dead' },
  NO_ANSWER:   { bg: 'var(--category-no-answer-bg)', color: 'var(--category-no-answer-fg)', label: 'No Answer' },
  BUSY:        { bg: 'var(--category-busy-bg)', color: 'var(--category-busy-fg)', label: 'Busy' },
  UNKNOWN:     { bg: 'var(--category-unknown-bg)', color: 'var(--category-unknown-fg)', label: 'Unknown' },
};

const CategoryBadge: React.FC<{ category: string; variant?: 'bold' | 'subtle' }> = ({ category, variant = 'subtle' }) => {
  const style = CATEGORY_STYLE[category?.toUpperCase()] || CATEGORY_STYLE.UNKNOWN;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 20,
      fontSize: 10, fontWeight: variant === 'bold' ? 700 : 600,
      background: style.bg, color: style.color,
      border: variant === 'bold' ? `1.5px solid ${style.color}33` : 'none',
    }}>
      {style.label}
    </span>
  );
};

const ProgressBadge: React.FC<{ status: string; errorNote?: string | null }> = ({ status, errorNote }) => {
  const cfg: Record<string, { bg: string; color: string; label: string; pulse?: boolean }> = {
    queued:       { bg: 'var(--status-analysis-queued-bg)', color: 'var(--status-analysis-queued-fg)', label: 'Queued' },
    processing:   { bg: 'var(--status-analysis-processing-bg)', color: 'var(--status-analysis-processing-fg)', label: 'Analyzing…', pulse: true },
    failed:       { bg: 'var(--status-analysis-failed-bg)', color: 'var(--status-analysis-failed-fg)', label: 'Failed' },
    no_recording: { bg: 'var(--status-analysis-no-recording-bg)', color: 'var(--status-analysis-no-recording-fg)', label: 'No Recording' },
    pending:      { bg: 'var(--status-analysis-pending-bg)', color: 'var(--status-analysis-pending-fg)', label: 'Pending' },
  };
  const c = cfg[status] || cfg.pending;

  const badge = (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600,
      background: c.bg, color: c.color,
      cursor: errorNote ? 'help' : 'default',
    }}>
      {c.pulse && (
        <span style={{
          width: 5, height: 5, borderRadius: '50%', background: c.color,
          display: 'inline-block', animation: 'pulse-dot 1.2s ease-in-out infinite',
        }} />
      )}
      {c.label}
      {errorNote && <span style={{ fontSize: 10 }}>ⓘ</span>}
    </span>
  );

  if (!errorNote) return badge;
  return (
    <Tooltip content={<><strong>Error</strong><br />{errorNote}</>} maxWidth={300}>
      {badge}
    </Tooltip>
  );
};

const ConfidenceBadge: React.FC<{ value: number }> = ({ value }) => {
  const color = value >= 80 ? 'var(--status-success-fg)' : value >= 50 ? 'var(--category-voicemail-fg)' : 'var(--status-analysis-failed-fg)';
  const bg    = value >= 80 ? 'var(--status-success-bg)' : value >= 50 ? 'var(--category-voicemail-bg)' : 'var(--status-analysis-failed-bg)';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 20,
      fontSize: 10, fontWeight: 700, background: bg, color,
    }}>
      {value}%
    </span>
  );
};

const StatChip: React.FC<{ label: string; value: number; color: string; bg: string; pulse?: boolean }> = ({
  label, value, color, bg, pulse,
}) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 9px', borderRadius: 20,
    fontSize: 11, fontWeight: 600, background: bg, color,
  }}>
    {pulse && value > 0 && (
      <span style={{
        width: 5, height: 5, borderRadius: '50%', background: color,
        display: 'inline-block', animation: 'pulse-dot 1.5s ease-in-out infinite',
      }} />
    )}
    {value} {label}
  </span>
);
